import type {
  CashflowCategory,
  CashflowDirection,
  CashflowEntry,
  CashflowFirmness,
  CashflowOrg,
  CashflowRuleKind,
} from "@/types/api";

/** Ключ вкладки/календаря «все организации» (так его помечает бэк). */
export const ALL_ORGS = "*";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Сегодня в формате `YYYY-MM-DD` (локальная дата, без сдвига через UTC). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Дата `YYYY-MM-DD` → UTC-`Date`; чужой формат или несуществующий день → `Error`.
 *
 * Молча вернуть `NaN`/сегодня нельзя: календарь получает недели с бэка, и битая
 * дата — это рассинхрон, который должен быть виден сразу.
 */
function parseIsoDate(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m === null) throw new Error(`Дата «${value}» не в формате 2026-09-14`);
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  // Проверка на 31.02 и т.п.: `Date.UTC` такие даты молча переносит вперёд.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) {
    throw new Error(`Даты «${value}» не существует (формат 2026-09-14)`);
  }
  return dt;
}

/**
 * Номер недели по ISO-8601 (неделя с четвергом внутри): `2026-09-14` → `38`.
 *
 * На входе понедельник недели (как ключ недели у бэка), но считается для любого дня.
 */
export function isoWeekNumber(monday: string): number {
  const date = parseIsoDate(monday);
  // Четверг этой недели задаёт ISO-год недели.
  const thursday = new Date(date.getTime());
  thursday.setUTCDate(thursday.getUTCDate() - ((thursday.getUTCDay() + 6) % 7) + 3);
  // 4 января всегда в первой неделе ISO-года — от её четверга и считаем.
  const jan4 = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  const firstThursday = new Date(jan4.getTime());
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

/** Заголовок колонки недели с номером: `2026-09-14` → `W38 · 14.09–20.09`. */
export function weekHeader(monday: string): string {
  return `W${isoWeekNumber(monday)} · ${weekLabel(monday)}`;
}

/** Заголовок колонки недели: понедельник `2026-09-14` → `14.09–20.09`. */
export function weekLabel(monday: string): string {
  const parts = monday.split("-").map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || y === undefined || m === undefined || d === undefined) return monday;
  const start = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(start.getTime())) return monday;
  const end = new Date(start.getTime() + 6 * 86_400_000);
  return (
    `${pad2(start.getUTCDate())}.${pad2(start.getUTCMonth() + 1)}` +
    `–${pad2(end.getUTCDate())}.${pad2(end.getUTCMonth() + 1)}`
  );
}

/** Суммы таблицы: без копеек и без «₽» — в календаре важен порядок, а не копейка. */
const compactFmt = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });

/**
 * Сумма-строка с бэка → `1 234 568` (NBSP-разделитель, как у `formatRub`).
 *
 * Пусто или не число → «—», как в `formatRub`: рассинхрон с бэком должен быть
 * виден, а не подменяться нулём.
 */
export function formatRubCompact(value: string | undefined): string {
  if (value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return compactFmt.format(num);
}

interface HttpErrorShape {
  response?: { status?: number; data?: { detail?: unknown } };
}

/** HTTP-код ошибки axios (или `null`, если её там нет). */
export function errorStatus(err: unknown): number | null {
  return (err as HttpErrorShape)?.response?.status ?? null;
}

interface PydanticIssue {
  loc?: unknown;
  msg?: unknown;
}

/** `loc` вида `["body", "amount"]` → «amount» (служебные `body`/`query` опускаем). */
function issueField(loc: unknown): string {
  if (!Array.isArray(loc)) return "";
  const parts = loc
    .filter((p) => p !== "body" && p !== "query" && p !== "path")
    .map((p) => String(p));
  return parts.join(".");
}

/** Массив ошибок валидации pydantic → «поле: сообщение; …» (или `null`, если формат чужой). */
function pydanticDetail(detail: unknown): string | null {
  if (!Array.isArray(detail) || detail.length === 0) return null;
  const lines: string[] = [];
  for (const raw of detail) {
    if (typeof raw !== "object" || raw === null) return null;
    const issue = raw as PydanticIssue;
    if (typeof issue.msg !== "string") return null;
    const field = issueField(issue.loc);
    lines.push(field ? `${field}: ${issue.msg}` : issue.msg);
  }
  return lines.join("; ");
}

/**
 * Текст ошибки для баннера/тоста: `detail` FastAPI как есть.
 *
 * Обычно это строка; массив ошибок валидации pydantic разворачиваем в
 * «поле: сообщение», а всё остальное показываем JSON-ом — причина должна быть
 * видна, а не подменяться общей фразой.
 */
export function errorDetail(err: unknown): string {
  const detail = (err as HttpErrorShape)?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const pydantic = pydanticDetail(detail);
  if (pydantic !== null) return pydantic;
  if (detail !== undefined && detail !== null) return JSON.stringify(detail);
  return String(err);
}

/** Строка-Decimal с бэка (`"-1234.50"`) → целые копейки; чужой формат → `null`. */
function toKopecks(value: string): bigint | null {
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
  if (m === null) return null;
  const kopecks = BigInt(m[2] as string) * 100n + BigInt((m[3] ?? "").padEnd(2, "0"));
  return m[1] === "-" ? -kopecks : kopecks;
}

/** Копейки → строка-Decimal ровно с двумя знаками: `12345n` → `"123.45"`. */
function fromKopecks(total: bigint): string {
  const sign = total < 0n ? "-" : "";
  const abs = total < 0n ? -total : total;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, "0")}`;
}

/**
 * Сумма строк-Decimal в целых копейках (без float): `["1", "2.5"]` → `"3.50"`.
 *
 * `undefined` или строка не того формата в массиве → `undefined`: это рассинхрон
 * с бэком, и в ячейке должно стоять «—», а не правдоподобное неверное число.
 */
export function sumDecimalStrings(values: (string | undefined)[]): string | undefined {
  let total = 0n;
  for (const value of values) {
    if (value === undefined) return undefined;
    const kopecks = toKopecks(value);
    if (kopecks === null) return undefined;
    total += kopecks;
  }
  return fromKopecks(total);
}

/** Сумма-строка с бэка — ноль? (для приглушения пустых ячеек) */
export function isZeroAmount(value: string | undefined): boolean {
  return value !== undefined && Number(value) === 0;
}

/** Подпись организации: имя из 1С, если календарь его дал, иначе просто ИНН. */
export function orgLabel(inn: string, orgs: CashflowOrg[]): string {
  const found = orgs.find((o) => o.inn === inn);
  return found ? found.name : `ИНН ${inn}`;
}

/**
 * Выбранная ячейка календаря — что показывать в детализации.
 *
 * `category === null` — все статьи недели, `direction === null` — оба
 * направления (строка «Остаток на конец»).
 */
export interface CashflowCellSelection {
  category: string | null;
  direction: CashflowDirection | null;
  weekIndex: number;
}

/** Короткие подписи твёрдости — для бейджа в таблице записей. */
export const FIRMNESS_SHORT: Record<string, string> = {
  committed: "Твёрдо",
  planned: "План",
  assumption: "Оценка",
};

/** Подписи твёрдости записи (совпадают со значениями `Firmness` на бэке). */
export const FIRMNESS_OPTIONS: { value: CashflowFirmness; label: string }[] = [
  { value: "committed", label: "Твёрдо (есть документ)" },
  { value: "planned", label: "План" },
  { value: "assumption", label: "Оценка" },
];

export const RULE_KIND_OPTIONS: { value: CashflowRuleKind; label: string }[] = [
  { value: "monthly", label: "Ежемесячно (день месяца)" },
  { value: "weekly", label: "Еженедельно (день недели)" },
];

/** 0 = понедельник — как в ядре (`engine.expand_series`). */
export const WEEKDAY_OPTIONS = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
].map((label, value) => ({ value, label }));

/** Сумма из поля ввода: запятая → точка, пробелы и NBSP убираем. */
export function normalizeAmount(raw: string): string {
  // `\s` в JS покрывает и обычный пробел, и NBSP из копипасты банковской выписки.
  return raw.replace(/\s/g, "").replace(",", ".");
}

/** Сумма строго > 0 и не больше двух знаков после запятой (как `Numeric(14, 2)`). */
export function isValidAmount(raw: string): boolean {
  const v = normalizeAmount(raw);
  if (!/^\d{1,12}(\.\d{1,2})?$/.test(v)) return false;
  return Number(v) > 0;
}

/** Статьи, разложенные по направлению — для `<optgroup>` «Приход» / «Расход». */
export function groupCategories(categories: CashflowCategory[]): {
  in: CashflowCategory[];
  out: CashflowCategory[];
} {
  return {
    in: categories.filter((c) => c.direction === "in"),
    out: categories.filter((c) => c.direction === "out"),
  };
}

/**
 * Отбор записей календаря под ячейку/строку таблицы.
 *
 * Одна логика на детализацию (`EntriesDialog`) и на дерево строк таблицы,
 * чтобы суммы подстрок не разошлись с диалогом. `undefined`/`null` в поле
 * фильтра — «без ограничения»; `orgInn === ALL_ORGS` — все организации.
 */
export function filterEntries(
  entries: CashflowEntry[],
  filter: {
    week?: string | undefined;
    category?: string | null;
    direction?: CashflowDirection | null;
    orgInn: string;
  },
): CashflowEntry[] {
  return entries.filter(
    (e) =>
      (filter.week === undefined || e.week === filter.week) &&
      (filter.category === undefined || filter.category === null || e.category === filter.category) &&
      (filter.direction === undefined ||
        filter.direction === null ||
        e.direction === filter.direction) &&
      (filter.orgInn === ALL_ORGS || e.org_inn === filter.orgInn),
  );
}

/** Группа записей для подстроки таблицы: суммы по неделям + итог. */
export interface CashflowEntryGroup {
  key: string;
  label: string;
  /** Суммы по неделям горизонта (в порядке `weeks`). */
  cells: (string | undefined)[];
  /** Итог по ВСЕМ записям группы — не по ячейкам: запись вне недель видна расхождением. */
  total: string | undefined;
  entries: CashflowEntry[];
}

/**
 * Записи → группы по ключу (контрагент, документ), суммы по неделям, сортировка по итогу.
 *
 * Итог группы считается по всем её записям, а не по сумме ячеек: запись с
 * неделей вне горизонта иначе исчезла бы молча.
 */
export function groupEntriesByWeek(
  entries: CashflowEntry[],
  weeks: string[],
  labelOf: (entry: CashflowEntry) => string,
): CashflowEntryGroup[] {
  const byKey = new Map<string, CashflowEntry[]>();
  for (const entry of entries) {
    const key = labelOf(entry);
    const bucket = byKey.get(key);
    if (bucket === undefined) byKey.set(key, [entry]);
    else bucket.push(entry);
  }
  const groups = [...byKey.entries()].map(([key, items]) => ({
    key,
    label: key,
    cells: weeks.map((w) =>
      sumDecimalStrings(items.filter((e) => e.week === w).map((e) => e.amount)),
    ),
    total: sumDecimalStrings(items.map((e) => e.amount)),
    entries: items,
  }));
  groups.sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0));
  return groups;
}

/** Разность строк-Decimal в копейках; чужой формат/`undefined` → `undefined`. */
export function diffDecimalStrings(
  minuend: string | undefined,
  subtrahend: string | undefined,
): string | undefined {
  if (minuend === undefined || subtrahend === undefined) return undefined;
  const a = toKopecks(minuend);
  const b = toKopecks(subtrahend);
  if (a === null || b === null) return undefined;
  return fromKopecks(a - b);
}

/**
 * Разность строк-Decimal, но не меньше нуля (например «без плана» = долг − план
 * − просрочено, по построению не должна уходить в минус).
 *
 * Рассинхрон с бэком (чужой формат/`undefined`) остаётся видимым — `undefined`,
 * а не тихий ноль.
 */
export function diffDecimalStringsNonNegative(
  minuend: string | undefined,
  subtrahend: string | undefined,
): string | undefined {
  const diff = diffDecimalStrings(minuend, subtrahend);
  if (diff === undefined) return undefined;
  return diff.startsWith("-") ? "0.00" : diff;
}

/**
 * Остаток строки статьи, не разнесённый по группам подстрок.
 *
 * `undefined` в ячейке (рассинхрон с бэком) считаем расхождением, а не нулём.
 */
export function undistributedCells(
  rowCells: (string | undefined)[],
  groups: CashflowEntryGroup[],
  weeks: string[],
): (string | undefined)[] {
  return weeks.map((_, i) =>
    diffDecimalStrings(
      rowCells[i],
      sumDecimalStrings(groups.map((g) => g.cells[i])),
    ),
  );
}

/** Есть ли в наборе сумм хоть одна ненулевая (или нечитаемая) — показывать ли расхождение. */
export function hasNonZeroAmount(values: (string | undefined)[]): boolean {
  return values.some((v) => v === undefined || !isZeroAmount(v));
}

/** Компактные отступы ячеек: недель много, а строк — два десятка. */
export const CELL_PAD = "px-2 py-1";
export const NUM_CELL = "text-right tabular-nums whitespace-nowrap";

/**
 * Липкая ячейка (первая колонка «Статья» и последняя «Итого»).
 *
 * Фон обязан быть НЕПРОЗРАЧНЫМ: под липкой ячейкой при горизонтальном скролле
 * едут цифры недель, и `bg-muted/40` их просвечивал.
 */
export function stickyCell(side: "left" | "right", emphasis: boolean): string {
  const pos = side === "left" ? "sticky left-0" : "sticky right-0 border-l";
  const bg = emphasis ? "bg-muted" : "bg-background";
  return `${pos} z-10 ${bg} group-hover:bg-muted`;
}

/**
 * Текст со списком ИНН (запятая/точка с запятой/пробел/перенос) → массив без дублей.
 *
 * Мусор не выбрасываем молча: любой токен не из 10/12 цифр → `Error` с его
 * текстом (в форме это ошибка поля). Пустой текст — пустой список: список
 * внутригрупповых ИНН может быть пуст.
 */
export function parseInnList(text: string): string[] {
  const tokens = text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  const bad = tokens.filter((t) => !/^(\d{10}|\d{12})$/.test(t));
  if (bad.length > 0) {
    throw new Error(`ИНН должен быть 10 или 12 цифр: ${bad.join(", ")}`);
  }
  return [...new Set(tokens)];
}
